import React from "react";
import { BlueprintVersionChanges, getChangeSummary } from "@/lib/utils/blueprintVersionUtils";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";
import { WizardMode } from "../types";

interface BlueprintVersionWarningProps {
  blueprintChanges: BlueprintVersionChanges;
  wizardMode: WizardMode;
  itemId?: string;
  onViewMigration?: () => void;
}

/**
 * Blueprint Version Warning component
 * Displays a warning banner when the object's blueprint version differs from the current blueprint
 */
export const BlueprintVersionWarning: React.FC<BlueprintVersionWarningProps> = ({
  blueprintChanges,
  wizardMode,
  itemId,
  onViewMigration,
}) => {
  const { t } = usePlatformTranslation();

  if (!blueprintChanges || !blueprintChanges.hasChanges) {
    return null;
  }

  return (
    <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <svg
            className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
            Blueprint Version Mismatch
          </h3>
          <div className="mt-1 text-sm text-amber-700 dark:text-amber-300">
            <p>
              {t("platform.wizards.glossary.OutOfSync")}
            </p>
            <p className="mt-1 font-medium">
              {getChangeSummary(blueprintChanges)}
            </p>
            {wizardMode === "view" ? (
              <p className="mt-2 text-xs">
                Switch to edit mode to see highlighted changes and
                interact with new fields.
              </p>
            ) : (
              <p className="mt-2 text-xs">
                New fields and updated properties are now available for
                editing. Save to apply the latest blueprint version.
              </p>
            )}
          </div>
          {itemId && onViewMigration && (
            <div className="mt-3">
              <button
                onClick={onViewMigration}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-500 text-white text-sm font-medium rounded transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                View Side-by-Side Comparison
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

